import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronDown, ChevronUp, Globe, Moon, Settings, Sun } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/Logo';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useSidebar } from '@/components/ui/use-sidebar';
import { useThemeAndLanguage } from '@/hooks/useThemeAndLanguage';
import { InstallPrompt } from '../pwa/InstallPrompt';
import { navigationItems } from './navigation-items';

export function AppSidebar() {
  const { t } = useTranslation('common');
  const router = useRouterState();
  const { isMobile, setOpenMobile } = useSidebar();
  const { theme, language, toggleTheme, changeLanguage } = useThemeAndLanguage();
  const currentPath = router.location.pathname;
  const [isLanguageOpen, setIsLanguageOpen] = React.useState(false);

  const isActive = (path: string) => {
    if (path === '/') return currentPath === '/';
    return currentPath.startsWith(path);
  };

  const handleNavigate = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const languageOptions: Array<{ value: 'en' | 'pt-BR'; label: string }> = [
    { value: 'en', label: t('language.en') },
    { value: 'pt-BR', label: t('language.pt-BR') },
  ];

  return (
    <>
      <SidebarHeader className="border-b border-sidebar-border">
        <Link
          to="/"
          onClick={handleNavigate}
          className="flex items-center gap-2 rounded-md px-2 py-1 text-sm font-semibold"
        >
          <Logo className="h-6 w-6 text-sidebar-foreground" />
          <span>{t('app.name')}</span>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map(({ path, icon: Icon, labelKey }) => (
                <SidebarMenuItem key={path}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(path)}
                    onClick={handleNavigate}
                    tooltip={t(labelKey)}
                  >
                    <Link to={path} className="flex items-center gap-2">
                      <Icon className="h-4 w-4 shrink-0" />
                      <span>{t(labelKey)}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>{t('settings.preferences.section')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  className="cursor-pointer"
                  onClick={toggleTheme}
                  tooltip={t('theme.label')}
                >
                  {theme === 'light' ? (
                    <Sun className="h-4 w-4 shrink-0" />
                  ) : (
                    <Moon className="h-4 w-4 shrink-0" />
                  )}
                  <span>{t('settings.preferences.theme')}</span>
                  <span className="ml-auto text-xs text-sidebar-foreground/70">
                    {theme === 'light' ? 'Light' : 'Dark'}
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
            <Collapsible open={isLanguageOpen} onOpenChange={setIsLanguageOpen}>
              <SidebarMenu>
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton className="cursor-pointer" tooltip={t('language.label')}>
                      <Globe className="h-4 w-4 shrink-0" />
                      <span>{t('language.select')}</span>
                      {isLanguageOpen ? (
                        <ChevronUp className="ml-auto h-4 w-4 shrink-0" />
                      ) : (
                        <ChevronDown className="ml-auto h-4 w-4 shrink-0" />
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                </SidebarMenuItem>
              </SidebarMenu>
              <CollapsibleContent>
                <SidebarMenu>
                  {languageOptions.map(({ value, label }) => (
                    <SidebarMenuItem key={value}>
                      <SidebarMenuButton
                        className="cursor-pointer"
                        onClick={() => changeLanguage(value)}
                        tooltip={t('language.label')}
                        isActive={language === value}
                      >
                        <Globe className="h-4 w-4 shrink-0" />
                        <span>{label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </CollapsibleContent>
            </Collapsible>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>{t('settings.title')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <InstallPrompt variant="sidebar" />
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActive('/settings')}
                  onClick={handleNavigate}
                  tooltip={t('settings.title')}
                >
                  <Link to="/settings" className="flex items-center gap-2">
                    <Settings className="h-4 w-4 shrink-0" />
                    <span>{t('settings.title')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </>
  );
}
